import { Theme } from "@mui/material";
import { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import IntervalType from "src/constants/intervalType.enum";
import { getWeekFromDate } from "src/lib/date.helper";

export function dashboardChartTooltipDateFormatter(intervalType: IntervalType, tooltipDate: Dayjs, t: TFunction): string {
  switch (intervalType) {
    case IntervalType.IsoYear:
      return `${t("calendar week")} ${getWeekFromDate(tooltipDate.toDate())}`;
    case IntervalType.Month:
      return `${t(tooltipDate.format("MMM"))} ${tooltipDate.format("D")}`;
    case IntervalType.Week:
      return `${t("day:")} ${t(tooltipDate.format("ddd").toLowerCase())}`;
    case IntervalType.Day:
    case IntervalType.Hour:
      return tooltipDate.format("HH:mm");
    default:
      throw new Error("Unsupported interval");
  }
}

export function determineColor(diff: number, theme: Theme): string {
  if (diff > 0) return theme.palette.positive.text;
  if (diff < 0) return theme.palette.negative.text;
  return theme.palette.text.secondary;
}

export function determineArrow(diff: number): string {
  if (diff > 0) return "↑";
  if (diff < 0) return "↓";
  return "";
}

export function getUnicodeSymbol(type: string): string {
  if (type === "NA") return "";

  switch (type) {
    case "clear-day":
      return "\uF00D";
    case "clear-night":
      return "\uF00D"; // "\uf02e";
    case "cloudy":
      return "\uF002";
    case "fog":
      return "\uF003";
    case "partly-cloudy-day":
      return "\uF083";
    case "partly-cloudy-night":
      return "\uF083"; // "\uf086";
    case "rain":
      return "\uF019";
    case "sleet":
      return "\uF0B5";
    case "snow":
      return "\uF01B";
    case "wind":
      return "\uF021";
    default:
      return "";
  }
}

export function axisDateFormatter(intervalType: IntervalType, date: Dayjs, t: TFunction): string {
  switch (intervalType) {
    case IntervalType.IsoYear:
      return getWeekFromDate(date.toDate()).toString();
    case IntervalType.Month:
      return t(date.format("D").toLowerCase());
    case IntervalType.Week:
      return t(date.format("ddd").toLowerCase());
    case IntervalType.Day:
    case IntervalType.Hour:
      return date.format("HH:mm");
    default:
      throw new Error("Unsupported interval");
  }
}

export function axisFormatter(startDate: string, date: Dayjs, t: TFunction): string {
  if (date.date() === 1 || date.format("YYYY-MM-DDTHH:mm:ss") === startDate) {
    return date.format("MMM D");
  }
  return t(date.format("D").toLowerCase());
}

export function createCustomEventIcon(type: string): string {
  if (!type) return "";

  switch (type) {
    case "neujahrstag":
    case "new-year":
      return "\uE803";
    case "berchtoldstag":
      return "\uE805";
    case "good-friday":
    case "karfreitag":
    case "auffahrt":
    case "pfingstmontag":
      return "\uE82F";
    case "bundesfeier":
      return "\uE811";
    case "ostermontag":
    case "easter":
      return "\uE80B";
    case "public-gathering":
      return "\uE801";
    case "christmas":
    case "weihnachtstag":
      return "\uE802";
    case "stephanstag":
    case "st-stephens-day":
      return "\uE806";
    case "zuri-fascht-tag":
      return "\uE800";
    case "tag-der-arbeit":
      return "\uE810";
    case "street-parade-zurich":
      return "\uE809";
    case "football":
      return "\uF1E3";
    default:
      return "\uE801";
  }
}
